import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import MenuToggle from './MenuToggle';
import Menu from './Menu';
import '../styles/QuantumFrameHeader.css';

const QuantumFrameHeader = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();

  const controlNavbar = useCallback(() => {
    // Don't hide header when menu is open
    if (isOpen) return;
    
    const scrollY = window.scrollY;
    const scrollThreshold = 50;

    if (scrollY > lastScrollY && scrollY > scrollThreshold) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    setLastScrollY(scrollY);
  }, [lastScrollY, isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isOpen) {
        requestAnimationFrame(controlNavbar);
      }
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [controlNavbar, isOpen]);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const currentScrollY = window.scrollY;
      setScrollPosition(currentScrollY);
      
      document.body.classList.add('menu-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.classList.remove('menu-open');
      
      // Restore the scroll position
      window.scrollTo(0, scrollPosition);
    }

    return () => {
      document.body.classList.remove('menu-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen, scrollPosition]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="qf-header-container">
        <motion.header
          className={`qf-header ${isVisible ? 'qf-visible' : 'qf-hidden'} ${isOpen ? 'menu-open' : ''}`}
          initial={{ y: 0, opacity: 1 }}
          animate={{
            y: isVisible || isOpen ? 0 : -100,
            opacity: isVisible || isOpen ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
        >
          <Link to="/" className="qf-logo" onClick={() => isOpen && setIsOpen(false)}>
            <img src="/assets/images/LOGO.png" alt="QuantumFrame Studio" className="qf-logo-image" />
          </Link>

          <nav className="qf-nav-desktop">
            <Link to="/" className="qf-nav-link">
              <span className="nav-text">Home</span>
              <span className="nav-highlight"></span>
            </Link>
            <Link to="/services" className="qf-nav-link">
              <span className="nav-text">Services</span>
              <span className="nav-highlight"></span>
            </Link>
            <Link to="/case-studies" className="qf-nav-link">
              <span className="nav-text">Case Studies</span>
              <span className="nav-highlight"></span>
            </Link>
            <Link to="/about-us" className="qf-nav-link">
              <span className="nav-text">About</span>
              <span className="nav-highlight"></span>
            </Link>
            <Link to="/contact" className="qf-nav-link">
              <span className="nav-text">Contact</span>
              <span className="nav-highlight"></span>
            </Link>
            <a href="/contact" className="qf-nav-cta">Get a Quote</a>
          </nav>

          {isMobile && <MenuToggle toggle={toggleMenu} isOpen={isOpen} />}
        </motion.header>
      </div>

      <AnimatePresence>
        {isOpen && isMobile && (
          <>
            <motion.div
              className="menu-background"
              onClick={toggleMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <Menu toggle={toggleMenu} isOpen={isOpen} />
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuantumFrameHeader;